import { ComponentWithTooltip } from 'components/ComponentWithTooltip';
import { getContractStatusLabel } from 'components/ContractStatus';
import { ErrorHandlingModal } from 'components/ErrorHandlingModal';
import { ControlledDateField, ControlledSelectField, ControlledTextField, ControlNumberField } from 'components/Form';
import { ControlledTabs } from 'components/Form/ControlledTab';
import { ModalDialog } from 'components/ModalDialog';
import { Viewport } from 'components/Viewport';
import { FIELD_WIDTH } from 'constantsProject';
import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import type {
    ConstructionTypeAttributeData,
    ContractConversionIndexData,
    ContractSeasonInflationData,
    ContractResultsViewData,
} from 'gql/graphql';
import { WorkOrderStatusEnum, ContractStatusEnum } from 'gql/graphql';
import type { IOptionsUseIsUniqInternalNumber } from 'hooks/useIsUniqInternalNumber';
import { useIsUniqInternalNumber } from 'hooks/useIsUniqInternalNumber';
import { useProjectData } from 'hooks/useProjectData';
import { useRunAfterSuccessRequest, useRunWithChangeCheck } from 'hooks/useRunWithChangeCheck';
import { useErrorTooltipsVisibility } from 'pages/Project/Common/hooks/useErrorTooltipsVisibility';
import { TabsIds } from 'pages/Project/Common/types';
import { contractTabs, costRoundingItems } from 'pages/Project/Common/utils';
import { PriceOptions } from 'pages/Project/UerProject/PriceOptions/PriceOptions';
import { Grid, GridItem, CollapseGroup, IconQuestion, Collapse } from 'uikit';
import { getLengthValidator } from 'utils';
import { ExportCSVReport } from 'utils/createCSVReportFile';
import { disableKeyButNumber } from 'utils/disableKeyButNumber';
import { getDateIncludingCurrentTime } from 'utils/formatDateToLocale';
import { replacerCost } from 'utils/replacerCost';
import { accessorAsName } from 'utils/tableCells';

import * as R from 'ramda';
import type React from 'react';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { roundingTooltip } from '../ConractRead/ContractReadForm';
import { ContractAccumulationSheetGrid } from '../ContractAccumulationSheetGrid/ContractAccumulationSheetGrid';
import { isUniqContractInternalNumberGql, updateContractGql } from '../ContractQueries';
import { useContractResultsData } from '../hooks/useContractData';
import type { ParsedContractData } from '../utils';
import { contractPlansContractPlanMapper, prepareContractData } from '../utils';

import { useTabs } from './hooks/useTabs';
import { useViewport } from './hooks/useViewport';
import {
    conversionCoefficientsKeys,
    conversionIndexInputToFlat,
    defaultToZero,
    getConstructionTypeCoefficientsKeys,
    nullsWithZeros,
} from './utils';

import Style from './ContractUpdateForm.module.css';

interface ContractUpdateFormProps {
    defaultValues: ParsedContractData;
    makeContractDataRequest: any;
}

const QuestionWithTooltip = () => (
    <ComponentWithTooltip tooltipText={roundingTooltip} styles={{ height: 16, marginLeft: 10 }} direction="rightCenter">
        <IconQuestion view="secondary" size="s" />
    </ComponentWithTooltip>
);

QuestionWithTooltip.displayName = 'QuestionWithTooltip';

export const visibilityHiddenStyle: React.CSSProperties = {
    visibility: 'hidden',
    position: 'absolute',
    overflow: 'hidden',
    maxHeight: 0,
};

export const ContractUpdateForm = forwardRef(
    ({ defaultValues: initialDefaultValues, makeContractDataRequest }: ContractUpdateFormProps, ref) => {
        const resultValueContractConversion = (item: number) => {
            if (item === 0) {
                return null;
            }

            return !item ? item : replacerCost(item);
        };

        const updateConversionIndicesData = useMemo(
            () => (initialDefaultValues?.conversionIndices || []).map((item) => nullsWithZeros(item)),
            [initialDefaultValues?.conversionIndices],
        );

        const defaultValues: ParsedContractData = useMemo(
            () => ({
                ...initialDefaultValues,
                conversionIndices: updateConversionIndicesData?.map((v, index) => ({ ...v, index })),
                seasonsInflation: initialDefaultValues?.seasonsInflation?.map((v, index) => ({ ...v, index })),
                ...contractPlansContractPlanMapper(initialDefaultValues?.contractPlans),
                constructionTypeAttributes: initialDefaultValues?.constructionTypeAttributes?.map((item) => ({
                    ...item,
                    northernAllowancePercent: defaultToZero(item.northernAllowancePercent),
                    uerOthersPercent: defaultToZero(item.uerOthersPercent),
                })),
            }),
            [initialDefaultValues, updateConversionIndicesData],
        );

        const { id: projectId } = useParams();
        const dispatch = useDispatch();
        const { control, handleSubmit, formState, setError, clearErrors, watch, setValue, resetField, getValues } =
            useForm({
                criteriaMode: 'all',
                defaultValues: defaultValues as any,
            });

        const { topIndention, debouncedScroll, isGroupContentScrolled, isMainFieldsOpen, switchMainFieldsOpen } =
            useViewport();

        const { projectData } = useProjectData(projectId, false);

        const statusContractInImplementation =
            projectData?.contractNavigation?.data?.contractNavData?.status === ContractStatusEnum.InImplementation;

        const { activeTab, handleTabChange } = useTabs();

        const isAccumulationSheetActive = activeTab?.id === TabsIds.ACCUMULATION_SHEET;

        const [gotAccumulationData, setGotAccumulationData] = useState(false);

        const hasWorkOrders = useMemo(
            () => !!projectData?.contractNavigation?.data?.workOrderNavs?.length,
            [projectData?.contractNavigation?.data?.workOrderNavs?.length],
        );

        const hasWorkOrdersInProgressOrCompleted = useMemo(
            () =>
                !!projectData?.contractNavigation?.data?.workOrderNavs?.filter((w) =>
                    [WorkOrderStatusEnum.InProgress, WorkOrderStatusEnum.Completed].includes(w.status),
                )?.length,
            [projectData?.contractNavigation?.data?.workOrderNavs],
        );

        const [
            contractResultsData,
            getContractResultsStatus,
            ,
            ,
            getContractResultsSetData,
            getContractResultsRefreshData,
        ] = useContractResultsData(
            defaultValues?.id,
            isAccumulationSheetActive && !gotAccumulationData && hasWorkOrders,
        );

        useRunWithChangeCheck({
            initialData: contractResultsData?.workOrders,
            uniqueIdentifier: 'id',
            compareKeys: ['status', 'internalNumber', 'signingDate'],
            compareDataRequestId: 'updateWorkOrder',
            action: getContractResultsRefreshData,
            withEffect: Boolean(contractResultsData?.workOrders),
        });

        useRunAfterSuccessRequest({
            compareDataRequestId: 'deleteWorkOrder',
            action: getContractResultsRefreshData,
            withEffect: Boolean(contractResultsData?.workOrders),
            additionalCompareKeys: ['id'],
        });

        const isLoading = getContractResultsStatus === RequestStatus.FETCHING;

        const contractResultData: ContractResultsViewData = useMemo(
            () =>
                hasWorkOrders
                    ? contractResultsData
                    : { contractResults: defaultValues?.contractPlans || [], workOrders: [] },
            [defaultValues?.contractPlans, contractResultsData, hasWorkOrders],
        );

        const internalNumber: string = useWatch({ control, name: 'internalNumber' });
        const finishDate = useWatch({ control, name: 'finishDate' });
        const startDate = useWatch({ control, name: 'startDate' });
        const statusField = useWatch({ control, name: 'status' });
        const costRounding = useWatch({ control, name: 'costRounding' });

        const optionsUniqInternalNumber: IOptionsUseIsUniqInternalNumber = useMemo(
            () => ({
                idData: { name: 'contractId', value: defaultValues?.id },
                request: { name: 'uerIsUniqueContractInternalNumber', value: isUniqContractInternalNumberGql },
                changedValue: { name: 'internalNumber', value: internalNumber },
            }),
            [internalNumber, defaultValues?.id],
        );
        const isUniqInternalNumber = useIsUniqInternalNumber(optionsUniqInternalNumber);

        useEffect(() => () => dispatch(getContractResultsSetData(undefined)), [dispatch, getContractResultsSetData]);

        useEffect(() => {
            if (contractResultsData && getContractResultsStatus === RequestStatus.SUCCESS) {
                setGotAccumulationData(true);
            }
        }, [contractResultsData, getContractResultsStatus]);

        useEffect(() => {
            if (internalNumber && defaultValues.internalNumber !== internalNumber && !isUniqInternalNumber) {
                setError('internalNumber', { message: 'Проект с таким внутренним номером уже существует' });
            } else {
                clearErrors('internalNumber');
            }
        }, [clearErrors, internalNumber, defaultValues.internalNumber, isUniqInternalNumber, setError]);

        useEffect(() => {
            if (finishDate && (finishDate?.getTime() || 0) - (startDate?.getTime() || 0) < 0) {
                setError('finishDate', { message: 'Дата окончания не может быть раньше даты начала' });
            } else {
                clearErrors('finishDate');
            }
        }, [clearErrors, finishDate, setError, startDate]);

        const [isSubmitted, setIsSubmitted] = useState(false);
        const navigate = useNavigate();

        const availableStatuses = useMemo(() => {
            const statusFollowers = (defaultValues?.statusFollowers || []).map((v) => ({
                id: v,
                label: getContractStatusLabel(v),
            }));

            return [...statusFollowers, ...(defaultValues?.status ? [defaultValues?.status] : [])];
        }, [defaultValues]);

        const [updateContractData, updateContractStatus, updateContractError, updateContractMakeRequest] = useRequest({
            requestId: 'updateContract',
            query: updateContractGql,
            withEffect: false,
        });

        const conversionIndicesFieldsNames = useMemo(
            () =>
                (updateConversionIndicesData || []).reduce(
                    (acc: string[], ci) => [...acc, ...getConstructionTypeCoefficientsKeys(ci.constructionType.id)],
                    [],
                ),
            [updateConversionIndicesData],
        );

        const seasonFieldNames = useMemo(
            () =>
                initialDefaultValues?.seasonsInflation?.map((v, index) =>
                    accessorAsName(['seasonsInflation', String(index), 'inflationRate']),
                ) ?? [],
            [initialDefaultValues?.seasonsInflation],
        );

        const constructionTypeAttributesNames = useMemo(
            () =>
                initialDefaultValues?.constructionTypeAttributes?.map((_, index) =>
                    accessorAsName(['constructionTypeAttributes', String(index), 'uerOthersPercent']),
                ) ?? [],
            [initialDefaultValues?.constructionTypeAttributes],
        );

        const onSubmit = useCallback(
            (data) => {
                const conversionIndexes =
                    data.conversionIndices?.map((ci: ContractConversionIndexData) => {
                        const { id } = ci.constructionType;

                        return {
                            constructionTypeId: id,
                            kemm: resultValueContractConversion(data[`kemm_${id}`]),
                            keqp: resultValueContractConversion(data[`keqp_${id}`]),
                            kmtr: resultValueContractConversion(data[`kmtr_${id}`]),
                            knr: resultValueContractConversion(data[`knr_${id}`]),
                            kozp: resultValueContractConversion(data[`kozp_${id}`]),
                            kpir: resultValueContractConversion(data[`kpir_${id}`]),
                            ksp: resultValueContractConversion(data[`ksp_${id}`]),
                        };
                    }) || [];

                const coefficientInflationRates =
                    data?.seasonsInflation?.map((si: ContractSeasonInflationData) => ({
                        inflationRate: replacerCost(si.inflationRate),
                        contractSeasonId: defaultValues?.seasons?.find((s) => s.year === si.year)?.id,
                    })) || [];

                const constructionTypeAttributes =
                    data?.constructionTypeAttributes?.map((ct: ConstructionTypeAttributeData) => ({
                        constructionTypeId: ct.constructionType.id,
                        northernAllowancePercent: replacerCost(ct.northernAllowancePercent),
                        uerOtherPercent: replacerCost(ct.uerOthersPercent),
                    })) || [];

                const priceParameters = defaultValues.isEditablePriceParams
                    ? {
                          conversionIndexes,
                          coefficientInflationRates,
                          constructionTypeAttributes,
                      }
                    : undefined;

                const signingDate = data.signingDate ? getDateIncludingCurrentTime(data.signingDate) : undefined;
                const startDateForm = data.startDate ? getDateIncludingCurrentTime(data.startDate) : undefined;
                const finishDateForm = data.finishDate ? getDateIncludingCurrentTime(data.finishDate) : undefined;

                // TODO: any
                const preparedData: any = prepareContractData({
                    ...R.omit(
                        [
                            'conversionIndices',
                            'constructionTypeAttributes',
                            'seasonsInflation',
                            'contractPlan',
                            'contractPlans',
                            ...conversionIndicesFieldsNames,
                        ],
                        data,
                    ),
                    amountWithoutTax: replacerCost(data.amountWithoutTax),
                    priceParameters,
                    signingDate,
                    startDate: startDateForm,
                    finishDate: finishDateForm,
                });

                dispatch(updateContractMakeRequest(preparedData));
                setIsSubmitted(true);
            },
            [
                conversionIndicesFieldsNames,
                defaultValues.isEditablePriceParams,
                defaultValues?.seasons,
                dispatch,
                updateContractMakeRequest,
            ],
        );

        const formRef = useRef<HTMLFormElement>(null);

        useImperativeHandle(ref, () => ({
            submit: () => {
                formRef.current?.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
            },
            isDirty: formState.isDirty,
        }));

        useEffect(() => {
            if (
                isSubmitted &&
                updateContractStatus === RequestStatus.SUCCESS &&
                updateContractData?.uerUpdateContract?.result
            ) {
                navigate(`../`, { state: { refreshTree: true } });
            }
        }, [
            defaultValues.id,
            isSubmitted,
            navigate,
            updateContractStatus,
            updateContractData?.uerUpdateContract?.result,
        ]);

        const parseValueNumber = useCallback((event) => disableKeyButNumber(event), []);
        const parseValueNumberAndPoint = useCallback((event) => disableKeyButNumber(event, true), []);

        const areRequiredDisabled = useMemo(() => !defaultValues?.seasons?.length, [defaultValues?.seasons?.length]);

        const commonGroups = useMemo(
            () => [
                {
                    label: 'Данные договора',
                    content: (
                        <Grid rowGap="s" cols="1">
                            <GridItem>
                                <ControlledTextField
                                    name="subject"
                                    label="Предмет"
                                    control={control}
                                    type="textarea"
                                    required
                                    rules={getLengthValidator(true, 1500)}
                                    disabled={areRequiredDisabled || hasWorkOrders || !statusContractInImplementation}
                                />
                            </GridItem>
                            <GridItem>
                                <ControlledTextField
                                    name="internalNumber"
                                    label="Внутренний номер"
                                    control={control}
                                    type="text"
                                    required
                                    rules={getLengthValidator(true)}
                                    disabled={areRequiredDisabled || hasWorkOrders || !statusContractInImplementation}
                                />
                            </GridItem>
                            <GridItem>
                                <ControlledTextField
                                    name="customer"
                                    label="Заказчик"
                                    control={control}
                                    type="text"
                                    disabled
                                />
                            </GridItem>
                            <GridItem>
                                <ControlledTextField
                                    name="customerAddress"
                                    label="Адрес заказчика"
                                    control={control}
                                    type="text"
                                    disabled={!statusContractInImplementation || hasWorkOrders}
                                    rules={getLengthValidator(false)}
                                />
                            </GridItem>
                            <GridItem>
                                <ControlledTextField
                                    name="contractor"
                                    label="Подрядчик"
                                    control={control}
                                    type="text"
                                    required
                                    rules={getLengthValidator(true)}
                                    disabled={areRequiredDisabled || hasWorkOrders || !statusContractInImplementation}
                                />
                            </GridItem>
                            <GridItem>
                                <ControlledTextField
                                    name="contractorAddress"
                                    label="Адрес подрядчика"
                                    control={control}
                                    type="text"
                                    disabled={!statusContractInImplementation}
                                    rules={getLengthValidator(false)}
                                />
                            </GridItem>
                        </Grid>
                    ),
                },
                {
                    label: 'Даты договора',
                    content: (
                        <Grid rowGap="s" cols="1">
                            <GridItem>
                                <ControlledDateField
                                    name="signingDate"
                                    label="Подписание"
                                    control={control}
                                    type="date"
                                    disabled={!statusContractInImplementation || hasWorkOrders}
                                />
                            </GridItem>
                            <GridItem>
                                <ControlledDateField
                                    name="startDate"
                                    label="Начало действия"
                                    control={control}
                                    type="date"
                                    disabled={!statusContractInImplementation || hasWorkOrders}
                                />
                            </GridItem>
                            <GridItem>
                                <ControlledDateField
                                    name="finishDate"
                                    label="Окончание действия"
                                    control={control}
                                    type="date"
                                    disabled={!statusContractInImplementation || hasWorkOrders}
                                />
                            </GridItem>
                        </Grid>
                    ),
                },
                {
                    label: 'Сумма договора',
                    content: (
                        <Grid rowGap="s" cols="1">
                            <GridItem>
                                <ControlNumberField
                                    name="cost.contractPlanWithTax"
                                    label="Сумма с НДС (руб.)"
                                    control={control}
                                    type="number"
                                    disabled
                                    labelPosition="left"
                                    width="full"
                                    size="s"
                                />
                            </GridItem>
                            <GridItem>
                                <ControlNumberField
                                    name="cost.contractPlan"
                                    label="Сумма без НДС (руб.)"
                                    control={control}
                                    type="number"
                                    labelPosition="left"
                                    width="full"
                                    size="s"
                                    disabled
                                />
                            </GridItem>
                            <GridItem>
                                <ControlNumberField
                                    name="cost.contractPlanTax"
                                    label="НДС, 20% (руб.)"
                                    control={control}
                                    type="number"
                                    disabled
                                    labelPosition="left"
                                    width="full"
                                    size="s"
                                />
                            </GridItem>
                        </Grid>
                    ),
                },
                {
                    label: 'Коды организации',
                    content: (
                        <Grid rowGap="s" cols="1">
                            <GridItem>
                                <ControlledTextField
                                    name="codeOKUD"
                                    label="Форма по ОКУД"
                                    control={control}
                                    onKeyDown={parseValueNumber}
                                    disabled={!statusContractInImplementation}
                                />
                            </GridItem>
                            <GridItem>
                                <ControlledTextField
                                    name="customerOKPO"
                                    label="По ОКПО Заказчик"
                                    control={control}
                                    onKeyDown={parseValueNumber}
                                    disabled={!statusContractInImplementation}
                                />
                            </GridItem>
                            <GridItem>
                                <ControlledTextField
                                    name="contractorOKPO"
                                    label="По ОКПО Подрядчик"
                                    control={control}
                                    onKeyDown={parseValueNumber}
                                    disabled={!statusContractInImplementation}
                                />
                            </GridItem>
                            <GridItem>
                                <ControlledTextField
                                    name="codeOKPD"
                                    label="Вид деятельности по ОКДП"
                                    control={control}
                                    onKeyDown={parseValueNumberAndPoint}
                                    disabled={!statusContractInImplementation}
                                />
                            </GridItem>
                        </Grid>
                    ),
                },
                {
                    label: 'Дополнительно',
                    content: (
                        <Grid rowGap="s" cols="1">
                            <GridItem>
                                <ControlledTextField
                                    name="note"
                                    label="Примечание"
                                    control={control}
                                    type="textarea"
                                    cols={50}
                                    rows={5}
                                    rules={{
                                        maxLength: {
                                            value: 255,
                                            message: 'не более 255 символов',
                                        },
                                    }}
                                    disabled={!statusContractInImplementation}
                                />
                            </GridItem>
                        </Grid>
                    ),
                },
            ],
            [
                areRequiredDisabled,
                control,
                hasWorkOrders,
                parseValueNumber,
                parseValueNumberAndPoint,
                statusContractInImplementation,
            ],
        );

        const commonGroupsOpened = useMemo(() => commonGroups.map((_, index) => index), [commonGroups]);

        useEffect(() => {
            const subscription = watch((value, { name, type }) => {
                if (name === 'amountWithoutTax' && type === 'change') {
                    const { amountWithoutTax } = value;

                    const amountWithoutTaxNum = amountWithoutTax && replacerCost(amountWithoutTax);
                    const tax = amountWithoutTaxNum * 0.2;

                    setValue('tax', tax);
                    setValue('amountWithTax', tax + amountWithoutTaxNum);
                }
            });

            return () => subscription.unsubscribe();
        }, [setValue, watch]);

        // изменение с "На реализации" на "Отложено" и обратно
        const isEqualsValuesStatus = statusField.id === defaultValues.status.id;
        const isContractStatusWork = statusField.id === ContractStatusEnum.InImplementation;
        const isContractStatusDelay = statusField.id === ContractStatusEnum.Delayed;
        const isContractStatusCompleted = statusField.id === ContractStatusEnum.Completed;
        const [isOpenConfirmationStatus, setIsOpenConfirmationStatus] = useState<boolean>(false);

        const handleConfirmConfirmationStatus = useCallback(() => setIsOpenConfirmationStatus(false), []);

        const handleCloseConfirmationStatus = useCallback(() => {
            resetField('status');
            setIsOpenConfirmationStatus(false);
        }, [resetField]);

        useEffect(() => {
            if (
                (isContractStatusWork && !isEqualsValuesStatus && formState.isDirty) ||
                (isContractStatusDelay && !isEqualsValuesStatus && formState.isDirty) ||
                (isContractStatusCompleted && !isEqualsValuesStatus && formState.isDirty)
            ) {
                setIsOpenConfirmationStatus(true);
            }
        }, [
            isContractStatusWork,
            isContractStatusDelay,
            isContractStatusCompleted,
            formState.isDirty,
            isEqualsValuesStatus,
        ]);

        const handleCloseModalError = useCallback(() => {
            setIsSubmitted(false);
        }, []);

        const handleCreateReport = () => {
            ExportCSVReport(updateContractData?.uerUpdateContract?.validation);
        };

        const priceCollapsesFieldsToCheck = useMemo(
            () => [
                {
                    index: 0,
                    fields: conversionIndicesFieldsNames,
                    errorMessage: 'Введите хотя бы один положительный индекс для каждого вида строительства',
                },
                {
                    index: 1,
                    fields: seasonFieldNames,
                    errorMessage: 'Поля обязательны для заполнения',
                },
                {
                    index: 2,
                    fields: constructionTypeAttributesNames,
                    errorMessage: 'Введите положительное значение процента прочих в УЕР для каждого вида строительства',
                },
            ],
            [constructionTypeAttributesNames, conversionIndicesFieldsNames, seasonFieldNames],
        );

        const { classNameTooltips, onTooltipAttributeChange } = useErrorTooltipsVisibility({ activeTab, formRef });

        const checkConversionCoefficientsByConstructionType = useCallback(
            (constructionTypeId: number) => {
                const data = getValues();

                const keysByType = getConstructionTypeCoefficientsKeys(constructionTypeId);

                const withValue = keysByType.some((key) => replacerCost(data[key]) > 0);

                if (!withValue) {
                    keysByType.forEach((key) => setError(key, { message: '' }));

                    return true;
                }

                keysByType.forEach((key) => clearErrors(key));

                return false;
            },
            [clearErrors, getValues, setError],
        );

        const constructionTypesFields = useWatch({ control, name: 'constructionTypes' });

        useEffect(() => {
            updateConversionIndicesData.forEach((rowItem) => {
                Object.entries(conversionIndexInputToFlat(rowItem)).forEach(([key, value]) => {
                    setValue(key, value);
                });
            });
        }, [setValue, updateConversionIndicesData]);

        useEffect(() => {
            const subscription = watch((value, { name, type }) => {
                if (name && type === 'change') {
                    const coefficient = conversionCoefficientsKeys.find((k) => name.startsWith(k));

                    if (coefficient) {
                        const constructionTypeId = Number(name.replace(/[^0-9-]+/g, ''));

                        if (constructionTypeId) {
                            checkConversionCoefficientsByConstructionType(constructionTypeId);
                        }
                    }
                }
            });

            return () => subscription.unsubscribe();
        }, [checkConversionCoefficientsByConstructionType, clearErrors, getValues, setError, setValue, watch]);

        const fieldLists = {
            [TabsIds.COMMON]: [
                'subject',
                'internalNumber',
                'customer',
                'customerAddress',
                'contractor',
                'contractorAddress',
                'signingDate',
                'startDate',
                'finishDate',
                'amountWithTax',
                'amountWithoutTax',
                'tax',
                'remainder',
                'note',
            ],
            [TabsIds.PRICE]: [
                'supplyBasis',
                ...conversionIndicesFieldsNames,
                ...seasonFieldNames,
                ...constructionTypeAttributesNames,
            ],
            [TabsIds.ACCUMULATION_SHEET]: [],
        };

        const onInvalidSubmit = useCallback(() => {
            constructionTypesFields?.some((ct: { id: number }) => checkConversionCoefficientsByConstructionType(ct.id));
        }, [checkConversionCoefficientsByConstructionType, constructionTypesFields]);

        return (
            <div className={Style.contractUpdateForm}>
                <form onSubmit={handleSubmit(onSubmit, onInvalidSubmit)} ref={formRef}>
                    <Collapse label="Основная информация" isOpen={isMainFieldsOpen} onClick={switchMainFieldsOpen}>
                        <Grid rowGap="s" cols="1">
                            <GridItem style={{ width: FIELD_WIDTH }}>
                                <ControlledSelectField
                                    name="status"
                                    label="Статус"
                                    control={control}
                                    placeholder="Статус"
                                    required
                                    items={availableStatuses}
                                    rules={{
                                        required: true,
                                    }}
                                />
                            </GridItem>
                            <GridItem style={{ width: FIELD_WIDTH }}>
                                <ControlledTextField
                                    name="authoredBy"
                                    label="Автор"
                                    control={control}
                                    type="text"
                                    placeholder="Автор"
                                    disabled
                                    required
                                />
                            </GridItem>
                            <GridItem style={{ width: FIELD_WIDTH }}>
                                <ControlledSelectField
                                    name="costRounding"
                                    label="Округление стоимости"
                                    control={control}
                                    placeholder="Округление стоимости"
                                    items={costRoundingItems}
                                    rules={{
                                        required: true,
                                    }}
                                    disabled={hasWorkOrders}
                                    labelIcon={QuestionWithTooltip}
                                    className="labelWithEndingIcon"
                                />
                            </GridItem>
                            <GridItem style={{ width: FIELD_WIDTH }}>
                                <ControlledSelectField
                                    name="catalogUerBase"
                                    label="Справочник расценок"
                                    control={control}
                                    items={[defaultValues?.catalogUerBase]}
                                    placeholder="Справочник расценок"
                                    getItemKey={(item) => item?.uniqueId ?? ''}
                                    getItemLabel={(item) => item?.name ?? ''}
                                    disabled
                                />
                            </GridItem>
                        </Grid>
                    </Collapse>
                    <GridItem style={{ height: 38 }}>
                        <ControlledTabs
                            value={activeTab}
                            onChange={handleTabChange}
                            items={contractTabs}
                            view="bordered"
                            size="s"
                            fieldsToCheck={fieldLists}
                            control={control}
                        />
                    </GridItem>
                    <div className={activeTab.id === TabsIds.ACCUMULATION_SHEET ? Style.viewportWrapper : ''}>
                        <Viewport
                            topIndention={topIndention}
                            marginBottom="var(--space-margin-bottom)"
                            onScroll={debouncedScroll}
                            className="viewport-container"
                        >
                            <CollapseGroup
                                items={commonGroups}
                                opened={commonGroupsOpened}
                                style={
                                    activeTab.id === TabsIds.COMMON
                                        ? { width: FIELD_WIDTH, margin: 'var(--space-margin-bottom)', marginLeft: 0 }
                                        : visibilityHiddenStyle
                                }
                            />
                            <div style={activeTab.id !== TabsIds.PRICE ? visibilityHiddenStyle : undefined}>
                                <PriceOptions
                                    control={control}
                                    defaultValues={defaultValues}
                                    isActive={activeTab.id === TabsIds.PRICE && !isGroupContentScrolled}
                                    isUpdateForm
                                    makeContractDataRequest={makeContractDataRequest}
                                    classNameTooltip={`${Style.customTooltip} ${classNameTooltips[TabsIds.PRICE]}`}
                                    fieldsToCheck={priceCollapsesFieldsToCheck}
                                    onTooltipAttributeChange={onTooltipAttributeChange}
                                />
                            </div>
                            <div
                                className={Style.wrapPriceOptions}
                                style={activeTab.id !== TabsIds.ACCUMULATION_SHEET ? visibilityHiddenStyle : undefined}
                            >
                                <ContractAccumulationSheetGrid
                                    contract={defaultValues}
                                    isLoading={isLoading}
                                    data={contractResultData}
                                    hasWorkOrders={hasWorkOrders}
                                    hasWorkOrdersInProgressOrCompleted={hasWorkOrdersInProgressOrCompleted}
                                    costRounding={costRounding?.id}
                                />
                            </div>
                        </Viewport>
                    </div>
                </form>
                <ModalDialog
                    title="Изменение статуса Договора"
                    isOpen={isOpenConfirmationStatus}
                    onConfirm={handleConfirmConfirmationStatus}
                    onClose={handleCloseConfirmationStatus}
                    btnConfirmLabel="Изменить"
                    btnCancelLabel="Не изменять"
                    withTitleCloseButton
                    style={{ width: 520 }}
                >
                    <div style={{ padding: 24 }}>
                        {!isContractStatusWork && (
                            <div style={{ marginBottom: 24 }}>
                                {isContractStatusDelay &&
                                    'Дальнейшая работа по Договору, в части создания Наряд-заказов и приемки объемов, будет недоступна.'}
                                {isContractStatusCompleted &&
                                    'После перевода в статус «Завершен» дальнейшая работа по договору будет недоступна.'}
                            </div>
                        )}
                        <div>
                            <span>Вы уверены, что хотите изменить статус на «</span>
                            <span style={{ whiteSpace: 'nowrap' }}>
                                {isContractStatusWork && 'На реализации'}
                                {isContractStatusDelay && 'Отложен'}
                                {isContractStatusCompleted && 'Завершен'}
                                »?
                            </span>
                        </div>
                    </div>
                </ModalDialog>
                <ErrorHandlingModal
                    titleText="Сохранение данных договора"
                    isSubmitted={isSubmitted}
                    statusRequest={updateContractStatus}
                    dataRequest={updateContractData?.uerUpdateContract}
                    errorsRequest={updateContractError}
                    handleCloseModalError={handleCloseModalError}
                    handleCreateReport={handleCreateReport}
                />
            </div>
        );
    },
);

ContractUpdateForm.displayName = 'ContractUpdateForm';
